import React from 'react';
import { Typography } from 'antd';
import styles from './StyleGuide.module.scss';

const { Title, Text, Paragraph } = Typography;

// Quick, temporary reference for the current typography ramp — not linked
// from any nav, reachable directly at ROUTES.STYLE_GUIDE. Sizes/weights are
// read live off the design tokens (via CSS vars), so this always reflects
// whatever tokens.ts currently defines rather than a hand-copied snapshot.
const FONT_SIZES: Array<{ token: string; px: string }> = [
  { token: '--font-size-2xs', px: '11px' },
  { token: '--font-size-xs', px: '12px' },
  { token: '--font-size-sm', px: '13px' },
  { token: '--font-size-base', px: '14px' },
  { token: '--font-size-md', px: '15px' },
  { token: '--font-size-lg', px: '16px' },
  { token: '--font-size-xl', px: '18px' },
  { token: '--font-size-2xl', px: '24px' },
  { token: '--font-size-3xl', px: '27px' },
  { token: '--font-size-4xl', px: '32px' },
  { token: '--font-size-5xl', px: '40px' },
];

const FONT_WEIGHTS: Array<{ token: string; value: string }> = [
  { token: '--font-weight-light', value: '300' },
  { token: '--font-weight-normal', value: '400' },
  { token: '--font-weight-medium', value: '500' },
  { token: '--font-weight-semibold', value: '600' },
  { token: '--font-weight-bold', value: '700' },
  { token: '--font-weight-extrabold', value: '800' },
];

const LINE_HEIGHTS: Array<{ token: string; value: string }> = [
  { token: '--line-height-tight', value: '1.2' },
  { token: '--line-height-snug', value: '1.375' },
  { token: '--line-height-normal', value: '1.5' },
  { token: '--line-height-relaxed', value: '1.625' },
  { token: '--line-height-loose', value: '2' },
];

const SAMPLE = 'The quick brown fox jumps over the lazy dog';

export const StyleGuide: React.FC = () => {
  return (
    <div className={styles.page}>
      <div className={styles.intro}>
        <Title level={2} className={styles.introTitle}>Typography reference</Title>
        <Text className={styles.introSub}>
          Quick, temporary page — live tokens, not a hand-typed snapshot. Not linked from any nav.
        </Text>
      </div>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Font families</Text>
        <div className={styles.stack}>
          <div className={styles.familyRow}>
            <Text className={styles.rowLabel}>--font-family-sans (body, h4–h6)</Text>
            <Text className={styles.sampleSans}>{SAMPLE}</Text>
          </div>
          <div className={styles.familyRow}>
            <Text className={styles.rowLabel}>--font-family-heading (display, h1–h3)</Text>
            <Text className={styles.sampleHeading}>{SAMPLE}</Text>
          </div>
          <div className={styles.familyRow}>
            <Text className={styles.rowLabel}>--font-family-mono (code)</Text>
            <Text className={styles.sampleMono}>{SAMPLE}</Text>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Headings (Typography.Title, as rendered app-wide)</Text>
        <div className={styles.stack}>
          <Title level={1} className={styles.headingSample}>Heading 1 — {SAMPLE}</Title>
          <Title level={2} className={styles.headingSample}>Heading 2 — {SAMPLE}</Title>
          <Title level={3} className={styles.headingSample}>Heading 3 — {SAMPLE}</Title>
          <Title level={4} className={styles.headingSample}>Heading 4 — {SAMPLE}</Title>
          <Title level={5} className={styles.headingSample}>Heading 5 — {SAMPLE}</Title>
          <h6 className={styles.h6Sample}>Heading 6 — {SAMPLE}</h6>
        </div>
      </section>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Brand expression (marketing/hero only)</Text>
        <div className={styles.stack}>
          <div className={`display-1 ${styles.rampRow}`}>Display 1 — {SAMPLE}</div>
          <div className={`display-2 ${styles.rampRow}`}>Display 2 — {SAMPLE}</div>
          <div className={`eyebrow ${styles.rampRow}`}>Eyebrow — {SAMPLE}</div>
        </div>
      </section>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Font size ramp</Text>
        <div className={styles.stack}>
          {FONT_SIZES.map(({ token, px }) => (
            <div key={token} className={styles.rampRow}>
              <span className={styles.rampLabel}>{token} <span className={styles.rampPx}>({px})</span></span>
              <span className={styles.rampSample} style={{ fontSize: `var(${token})` }}>{SAMPLE}</span>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Font weights</Text>
        <div className={styles.stack}>
          {FONT_WEIGHTS.map(({ token, value }) => (
            <div key={token} className={styles.rampRow}>
              <span className={styles.rampLabel}>{token} <span className={styles.rampPx}>({value})</span></span>
              <span className={styles.rampSample} style={{ fontWeight: `var(${token})` }}>{SAMPLE}</span>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Line heights</Text>
        <div className={styles.stack}>
          {LINE_HEIGHTS.map(({ token, value }) => (
            <div key={token} className={styles.rampRow}>
              <span className={styles.rampLabel}>{token} <span className={styles.rampPx}>({value})</span></span>
              <p className={styles.lineHeightSample} style={{ lineHeight: `var(${token})` }}>
                {SAMPLE}, {SAMPLE}, {SAMPLE}.
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Text colors / roles</Text>
        <div className={styles.stack}>
          <div className={styles.colorPrimary}>Primary — {SAMPLE}</div>
          <div className={styles.colorSecondary}>Secondary — {SAMPLE}</div>
          <div className={styles.colorTertiary}>Tertiary — {SAMPLE}</div>
          <div className={styles.colorLink}>Link — {SAMPLE}</div>
        </div>
      </section>

      <section className={styles.section}>
        <Text className={styles.sectionLabel}>Paragraph</Text>
        <Paragraph className={styles.paragraphSample}>
          {SAMPLE}. {SAMPLE}. {SAMPLE}. This paragraph uses the default body text style —
          font-size-base, line-height-normal, and the primary text color — to show how running
          copy actually reads at the current ramp, not just isolated one-line samples.
        </Paragraph>
      </section>
    </div>
  );
};

export default StyleGuide;
